import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import type { ModalProps } from '~/modal/modalType.enum';
import { DataLayerEditorModalContainer } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/modals/dataLayerEditorModal.container';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import type { DemographicRegion } from '~/store/demographics/demographics.types';
import { useDataLayersTemplates } from '~/store/frontendState/mapTools/dataLayers/useDataLayersTemplates';
import {
  mapSettingsDataLayersAddDataLayer, mapSettingsDataLayersUpdateBoundaryGroup,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import { createDataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.factory';
import type { DataLayerGroupData } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';

export type DataLayerCreateModalProps = {
  boundaryGroupId: BoundaryGroupId;
  initialRegion?: DemographicRegion;
};

export const DataLayerCreateModalContainer = ({
  boundaryGroupId,
  initialRegion,
  ...modalProps
}: ModalProps<DataLayerCreateModalProps>) => {
  const dispatch = useDispatch();
  const { createNewTemplate } = useDataLayersTemplates();

  const handleSubmit = useCallback((dataLayerData: DataLayerGroupData, alsoSaveTemplate?: boolean) => {
    const newLayer = createDataLayer({
      ...dataLayerData,
    });
    dispatch(mapSettingsDataLayersUpdateBoundaryGroup(newLayer.id, boundaryGroupId));
    dispatch(mapSettingsDataLayersAddDataLayer(newLayer));
    if (alsoSaveTemplate) {
      createNewTemplate(newLayer);
    }
    modalProps.onClose();
  }, [dispatch, boundaryGroupId, createNewTemplate, modalProps]);

  const initialData = useMemo(() => ({
    region: initialRegion,
  }), [initialRegion]);

  return (
    <DataLayerEditorModalContainer
      boundaryGroupId={boundaryGroupId}
      initialData={initialData}
      onSubmit={handleSubmit}
      {...modalProps}
    />
  );
};
